import React, {useEffect, useMemo, useState} from 'react';
import { Autocomplete, TextField, Box, Checkbox, Typography, CircularProgress, Chip } from '@mui/material';
import CustomListbox from '../../../components/CustomListbox';
import {
  isBusinessProfileRegistered,
  OVERALL_BUSINESS_PROFILE_STATUS_LABELS,
} from '../../../constants/businessProfileStatusConstants.jsx';

const getProfileOptionLabel = (option) => {
  const name = option?.party_name || option?.name || '';
  const email = option?.email || '';
  const tin = option?.tin || option?.party_tax_scheme?.company_id || '';
  return [name, email, tin].filter(Boolean).join(' | ');
};

const BusinessProfileMultiSelect = ({
  profiles = [],
  selected = [],
  isLoading = false,
  disabled = false,
  onChange,
  onInputChange,
  allProfilesLabel = 'All Business Profiles',
  selectedCountLabel,
  selectAllLabel,
  errors
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [profileCache, setProfileCache] = useState(new Map());

  useEffect(() => {
    setProfileCache((prev) => {
      const next = new Map(prev);

      profiles.forEach((profile) => {
        if (profile?.profile_id) {
          next.set(profile.profile_id, profile);
        }
      });

      return next;
    });
  }, [profiles]);

  const selectedObjects = useMemo(() => {
    return selected.map((id) => profileCache.get(id))
        .filter(Boolean);
  }, [selected, profileCache]);

  const selectedCount = selected.length;

  const countLabel =
    selectedCountLabel?.replace('{count}', String(selectedCount)) ??
    `${selectedCount} Business Profile(s) selected`;

  const closedLabel = selectedObjects.length === 0 ? allProfilesLabel : countLabel;

  const resolvedSelectAllLabel = (selectAllLabel ?? `Select All ${profiles.length} business profiles`).replace(
    '{count}',
    String(profiles.length),
  );

  return (
    <Box>
      <Autocomplete
        multiple
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={profiles}
        loading={isLoading}
        disabled={disabled || isLoading}
        value={selectedObjects}
        inputValue={inputValue}
        disableCloseOnSelect
        ListboxComponent={CustomListbox}
        ListboxProps={{
          selectAllLabel: resolvedSelectAllLabel,
          onSelectAll: () => onChange(profiles.map((profile) => profile.profile_id)),
          onClearAll: () => onChange([]),
          sx: {
            paddingTop: 0,
            maxHeight: 256,
            overflowY: 'auto',
          },
        }}
        onInputChange={(event, value, reason) => {
          if(reason === 'input'){
            setInputValue(value);
        }}}
        // onBlur={event => setInputValue("")}
        getOptionLabel={getProfileOptionLabel}
        filterOptions={(options, { inputValue }) => {
          const search = inputValue.trim().toLowerCase();

          if (!search) {
            return options;
          }

          return options.filter((option) =>
              option?.party_name.toLowerCase().includes(search) || option?.endpoint_id.toLowerCase().includes(search),
          );
        }}
        isOptionEqualToValue={(option, value) => option.profile_id === value.profile_id}
        onChange={(_, newValue) => {
          onChange(newValue.map((profile) => profile.profile_id));
        }}
        renderTags={() => {
          if (open) return null;
          return <span style={{ marginLeft: 8 }}>{closedLabel}</span>;
        }}
        renderOption={(props, option, { selected: isSelected }) => {
          const isIncomplete = !isBusinessProfileRegistered(option);

          return (
            <li {...props}>
              <Checkbox checked={isSelected} sx={{ marginRight: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography fontSize={14} fontWeight={500}>
                  {getProfileOptionLabel(option)}
                </Typography>
                {isIncomplete && (
                  <Chip
                    label={OVERALL_BUSINESS_PROFILE_STATUS_LABELS.PENDING}
                    size="small"
                    color="info"
                    sx={{ marginLeft: 'auto', fontSize: '10px', height: 20 }}
                  />
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={open ? 'Search Business Profile' : ''}
            error={errors}
            helperText={
                errors && selectedCount === 0
                    ? 'At least one Business Profile should be selected.'
                    : ''
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default BusinessProfileMultiSelect;
